import {
  BellRing,
  BriefcaseBusiness,
  Compass,
  History,
  FileStack,
  LogOut,
  Menu,
  Network,
  Search,
  Settings,
  UserRoundSearch,
} from 'lucide-react';
import {ReactNode, useEffect, useState} from 'react';
import {NavLink, useLocation} from '../router';
import {Badge} from '../design';
import {signOutPursuitDeck} from '../api';
import {BootstrapPayload} from '../types';
import '../workflows.css';
import {GlobalSearch} from './GlobalSearch';

type NavigationItem = {
  to: string;
  label: string;
  icon: typeof BriefcaseBusiness;
  end?: boolean;
};

const items: readonly NavigationItem[] = [
  {to: '/', label: 'Opportunities', icon: BriefcaseBusiness, end: true},
  {to: '/action-inbox', label: 'Action Inbox', icon: BellRing},
  {to: '/discover', label: 'Discover', icon: Compass},
  {to: '/relationships', label: 'Relationships', icon: Network},
  {to: '/career-profile', label: 'Career Profile', icon: UserRoundSearch},
  {to: '/documents', label: 'Documents', icon: FileStack},
  {to: '/settings', label: 'Settings', icon: Settings},
  {to: '/history', label: 'History', icon: History},
];

const currentTitle = (pathname: string): string => {
  const suffix = pathname.replace(/^\/?/, '');
  const match = items.find((item) => item.to.slice(1) === suffix);
  return match?.label ?? 'Opportunities';
};

export const Shell = ({
  bootstrap,
  children,
}: {
  bootstrap: BootstrapPayload;
  children: ReactNode;
}) => {
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const urgent = bootstrap.counts.actionItems;
  const mobilePrimaryItems = items.slice(0, 4);
  const mobileMoreItems = items.slice(4);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [mobileMenuOpen]);

  return (
    <div className="pd-app">
      <a className="pd-skip-link" href="#pursuitdeck-main">
        Skip to content
      </a>
      <aside className="pd-sidebar" aria-label="PursuitDeck">
        <div className="pd-brand">
          <div className="pd-brand-mark" aria-hidden="true">
            PD
          </div>
          <div>
            <strong>PursuitDeck</strong>
            <span>Private search desk</span>
          </div>
        </div>
        <nav className="pd-primary-nav" aria-label="PursuitDeck sections">
          {items.map(({to, label, icon: Icon, end}) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({isActive}) => 'pd-nav-link' + (isActive ? ' is-active' : '')}
            >
              <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
              <span>{label}</span>
              {label === 'Action Inbox' && urgent > 0 ? (
                <span className="pd-nav-count" aria-label={urgent + ' open actions'}>
                  {urgent}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="pd-sidebar-footer">
          <button type="button" className="pd-signout-link" onClick={() => void signOutPursuitDeck()}>
            <LogOut size={17} aria-hidden="true" />
            Sign out
          </button>
          <div className="pd-user-chip">
            <span>{(bootstrap.user.displayName || bootstrap.user.email).slice(0, 1).toUpperCase()}</span>
            <div>
              <strong>{bootstrap.user.displayName || bootstrap.user.email.split('@')[0]}</strong>
              <small>{bootstrap.user.accessKind === 'inherited_admin' ? 'Administrator access' : 'Beta candidate'}</small>
            </div>
          </div>
        </div>
      </aside>

      <div className="pd-main-column">
        <header className="pd-topbar">
          <div>
            <span className="pd-mobile-context">PursuitDeck</span>
            <h1>{currentTitle(location.pathname)}</h1>
            <button type="button" className="pd-mobile-signout-link" onClick={() => void signOutPursuitDeck()}>
              <LogOut size={15} aria-hidden="true" />
              Sign out
            </button>
          </div>
          <div className="pd-topbar-actions">
            {bootstrap.integrations.some((item) => item.state === 'stale' || item.state === 'error') ? (
              <Badge className="pd-status-badge pd-status-badge--warning">Sync needs attention</Badge>
            ) : (
              <Badge className="pd-status-badge pd-status-badge--quiet">Systems current</Badge>
            )}
            <button
              type="button"
              className="pd-icon-button"
              aria-label={searchOpen ? 'Close search' : 'Search PursuitDeck'}
              aria-expanded={searchOpen}
              onClick={() => setSearchOpen((value) => !value)}
            >
              <Search size={19} aria-hidden="true" />
            </button>
          </div>
          {searchOpen ? <GlobalSearch onClose={() => setSearchOpen(false)} /> : null}
        </header>
        <main id="pursuitdeck-main" className="pd-page" tabIndex={-1}>
          {children}
        </main>
      </div>

      <nav className="pd-mobile-nav" aria-label="PursuitDeck mobile sections">
        {mobilePrimaryItems.map(({to, label, icon: Icon, end}) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            aria-label={label === 'Relationships' ? label : undefined}
            className={({isActive}) => 'pd-mobile-link' + (isActive ? ' is-active' : '')}
          >
            <Icon size={19} aria-hidden="true" />
            <span>{
              label === 'Career Profile' ? 'Profile'
                : label === 'Action Inbox' ? 'Actions'
                  : label === 'Relationships' ? 'People' : label
            }</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={'pd-mobile-link pd-mobile-more-trigger' + (
            mobileMoreItems.some((item) => location.pathname.endsWith(item.to)) ? ' is-active' : ''
          )}
          aria-label="More PursuitDeck sections"
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen((value) => !value)}
        >
          <Menu size={19} aria-hidden="true" />
          <span>More</span>
        </button>
        {mobileMenuOpen ? (
          <div className="pd-mobile-more-menu" aria-label="Additional PursuitDeck sections">
            {mobileMoreItems.map(({to, label, icon: Icon, end}) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({isActive}) => 'pd-mobile-more-link' + (isActive ? ' is-active' : '')}
              >
                <Icon size={18} aria-hidden="true" />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        ) : null}
      </nav>
    </div>
  );
};
